import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { FormControl, FormsModule, ValidatorFn, Validators } from '@angular/forms'
import { animate, state, style, transition, trigger } from '@angular/animations'

import { CommonModule } from '@angular/common'
import { MatInput } from '@angular/material/input'
import { NgxMaskDirective } from 'ngx-mask'
import { SharedModule } from '@shared/shared.module'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import dayjs from 'dayjs'
import { getCurrencySymbol } from '@shared/utils/utils'
import { isNil } from 'lodash'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(customParseFormat)

@Component({
  selector: 'scope-ui-input',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, NgxMaskDirective],
  templateUrl: './scope-ui-input.component.html',
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({
          width: '200px',
        })
      ),
      state(
        'expanded',
        style({
          width: '400px',
        })
      ),
      transition('collapsed <=> expanded', [animate('300ms ease-in-out')]),
    ]),
  ],
})
export class ScopeUiInputComponent implements OnChanges, AfterViewInit {
  @Input() control: FormControl = new FormControl()
  @Input() inputLabel!: string
  @Input() inputPlaceholder?: string
  @Input() type?: 'text' | 'number'
  @Input() required = false
  @Input() isTextarea: boolean = false
  @Input() textareaRows?: number
  @Input() inputClass?: string
  @Input() autofocus?: boolean
  @Input() icon!: string
  @Input() cancelIcon!: string
  @Input() fetchIcon!: string
  @Input() resultsCount?: number
  @Input() minimumLength?: number
  @Input() maximumLength?: number
  @Input() minimumDate?: Date
  @Input() maximumDate?: Date
  @Input() parseNumber: boolean = false
  @Input() preventPaste: boolean = false
  @Input() autoExpand: boolean = false
  @Input() noArrows: boolean = false
  @Input() dateFormat: string
  @Input() separatorLimit: string
  @Input() allowNegativeNumbers: boolean = false

  @Input() set initialValue(value: string | number | null) {
    this.value = value
    this.control.setValue(value)
    this.cdr.detectChanges()
  }

  @Input() set isDisabled(value: boolean) {
    this.disabled = value
    this.setDisabledState()
  }

  @Input() set minimum(value: number) {
    this.setMinimum(value)
  }

  @Input() set maximum(value: number) {
    this.setMaximum(value)
  }

  @Input() set formSubmitted(value: boolean) {
    if (value) {
      this.control.updateValueAndValidity()
      this.control.markAsTouched()
    }
  }

  @Input() set isCurrency(value: boolean) {
    this._isCurrency = value
    if (value) this.setInputComponents('separator.2', this.currencySymbol, '')
  }
  @Input() set isPercentage(value: boolean) {
    this._isPercentage = value
    if (value) this.setInputComponents('separator.2', '', '%')
  }
  @Input() set isDate(value: boolean) {
    this._isDate = value
    if (value) this.setInputComponents('99/99/9999', '', '')
  }
  @Input() set isInteger(value: boolean) {
    if (value) {
      this.mask = '0*'
      if (this.control.value % 1) {
        let inputValue = Math.round(this.control.value)
        this.control.setValue(inputValue)
        this.onInputChange.emit(inputValue)
      }
    }

    if (!value && this._isInteger) this.mask = undefined

    this._isInteger = value
  }

  @Input() set decimalPlaces(value: number) {
    this.setDecimalPlaces(value)
  }

  @Input() set currency(value: string) {
    this.currencySymbol = getCurrencySymbol(value)
    this.prefix = this._isCurrency ? this.currencySymbol : ''
  }

  @Output() onInputChange = new EventEmitter<any>()
  @Output() onInputBlurChange = new EventEmitter<any>()
  @Output() onEnter = new EventEmitter<any>()
  @Output() onCancel = new EventEmitter<void>()
  @Output() onFetch = new EventEmitter<void>()

  @ViewChild(MatInput) input: MatInput

  @HostBinding('class.scope-input') bindClass = true

  private readonly BACKSPACE_KEY_CODE: number = 8

  currencySymbol: string
  disabled: boolean = false
  value: any
  mask: string
  suffix: string
  prefix: string
  minValidator: ValidatorFn
  maxValidator: ValidatorFn

  _isCurrency: boolean = false
  _isPercentage: boolean = false
  _isInteger: boolean = false
  _decimalPlaces: number
  _isDate: boolean = false
  _minimum: number
  _maximum: number

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.control) {
      this.setMinimum()
      this.setMaximum()
      this.setDecimalPlaces()
      this.validateDates()
      this.setDisabledState()
    }
  }

  ngOnInit() {
    this.setDisabledState()
    this.validateDates()
  }

  ngAfterViewInit() {
    if (this.autofocus) setTimeout(() => this.input.focus())
  }

  validateDates() {
    if (
      this.minimumDate &&
      dayjs.utc(this.value, this.dateFormat).format('YYYY-MM-DD') < dayjs.utc(this.minimumDate).format('YYYY-MM-DD')
    ) {
      this.value = dayjs.utc(this.minimumDate).format(this.dateFormat)
      this.control.setValue(this.value)
    }
    if (
      this.maximumDate &&
      dayjs.utc(this.value, this.dateFormat).format('YYYY-MM-DD') > dayjs.utc(this.maximumDate).format('YYYY-MM-DD')
    ) {
      this.value = dayjs.utc(this.maximumDate).format(this.dateFormat)
      this.control.setValue(this.value)
    }
  }

  setMinimum(value: number = this._minimum) {
    this._minimum = value
    this.control.removeValidators(this.minValidator)
    if (!isNil(this._minimum)) {
      this.minValidator = Validators.min(value)
      this.control.addValidators(this.minValidator)
    }
    this.control.updateValueAndValidity()
    console.info('minimum', value, this.control.hasValidator(this.minValidator))
  }

  setMaximum(value: number = this._maximum) {
    this._maximum = value
    this.control.removeValidators(this.maxValidator)
    if (!isNil(this._maximum)) {
      this.maxValidator = Validators.max(value)
      this.control.addValidators(this.maxValidator)
    }
    this.control.updateValueAndValidity()
    console.info('maximum', value, this.control.hasValidator(this.maxValidator))
  }

  setDecimalPlaces(value: number = this._decimalPlaces) {
    if (value !== undefined) {
      this.mask = `separator.${value}`
      if (value === 0 && this.control.value % 1) {
        let inputValue = Math.round(this.control.value)
        this.control.setValue(inputValue)
        this.onInputChange.emit(inputValue)
      }
    } else if (this._decimalPlaces !== undefined) {
      this.mask = undefined
    }
    this._decimalPlaces = value
  }

  setDisabledState() {
    if (this.disabled) this.control.disable()
    else this.control.enable()
  }

  setInputComponents(mask: string, prefix: string, suffix: string) {
    this.mask = mask
    this.prefix = prefix
    this.suffix = suffix
  }

  sliceValue(inputValue: string) {
    if (this.maximumLength && inputValue.length > this.maximumLength) {
      inputValue = inputValue.slice(0, this.maximumLength)
      this.control.setValue(inputValue)
    }

    return inputValue
  }

  onInput(event: Event) {
    let element = event.target as HTMLInputElement
    let inputValue = element.value

    if (this.autoExpand) {
      element.style.height = 'auto'
      element.style.height = `${element.scrollHeight}px`
    }

    if (this.parseNumber) {
      inputValue = this.sliceValue(inputValue)
      if (this._isCurrency && this.currencySymbol != '') inputValue = inputValue.replace(this.currencySymbol, '')
      let value = parseFloat(inputValue.replace(/,/g, ''))
      this.onInputChange.emit(!isNaN(value) ? value : undefined)
      return
    }

    if (this._isDate) {
      let date = dayjs.utc(inputValue, this.dateFormat)
      if (date.isValid()) {
        if (
          !(
            date.format('YYYY-MM-DD') >= dayjs.utc(this.minimumDate).format('YYYY-MM-DD') &&
            date.format('YYYY-MM-DD') <= dayjs.utc(this.maximumDate).format('YYYY-MM-DD')
          )
        ) {
          this.control.setValue(this.value)
        } else {
          this.value = inputValue
          this.onInputChange.emit(date.toDate())
        }
      }
      return
    }

    this.onInputChange.emit(this.sliceValue(inputValue))
  }

  onKeydown(event: KeyboardEvent) {
    if (this.isTextarea && event.key === 'Enter') return

    const inputValue = (event.target as HTMLInputElement).value
    this.onKeydownCheck(inputValue, event)

    if (event.key === 'Enter' || event.which === 13) {
      if (this.parseNumber) {
        this.onEnter.emit(!isNaN(parseFloat(inputValue)) ? parseFloat(inputValue) : undefined)
      } else if (this._isDate) {
        let date = dayjs.utc(inputValue, this.dateFormat)
        if (date.isValid()) {
          if (
            !(
              date.format('YYYY-MM-DD') >= dayjs.utc(this.minimumDate).format('YYYY-MM-DD') &&
              date.format('YYYY-MM-DD') <= dayjs.utc(this.maximumDate).format('YYYY-MM-DD')
            )
          ) {
            this.control.setValue(this.value)
          } else {
            this.value = inputValue
            this.onEnter.emit(date.toDate())
          }
        }
      } else {
        this.onEnter.emit(inputValue)
      }
      ;(event.target as HTMLInputElement).blur()
    }
  }

  onKeydownCheck(inputValue: string, event: KeyboardEvent) {
    if (
      (this.maximumLength && inputValue.length > this.maximumLength && event.which !== this.BACKSPACE_KEY_CODE) ||
      (this._minimum === 0 && event.key === '-') ||
      (this.type === 'number' && (event.key === 'e' || event.key === '+'))
    )
      event.preventDefault()
  }

  onInputBlur(event: Event) {
    let inputValue = (event.target as HTMLInputElement).value

    if (this.parseNumber) {
      if (this._isCurrency && this.currencySymbol != '') inputValue = inputValue.replace(this.currencySymbol, '')
      let value = parseFloat(inputValue.replace(/,/g, ''))
      this.onInputBlurChange.emit(!isNaN(value) ? value : undefined)
      return
    }

    if (this._isDate) {
      let date = dayjs.utc(inputValue, this.dateFormat)
      if (date.isValid()) {
        if (
          !(
            date.format('YYYY-MM-DD') >= dayjs.utc(this.minimumDate).format('YYYY-MM-DD') &&
            date.format('YYYY-MM-DD') <= dayjs.utc(this.maximumDate).format('YYYY-MM-DD')
          )
        ) {
          this.control.setValue(this.value)
        } else {
          this.value = inputValue
          this.onInputBlurChange.emit(date.toDate())
        }
      }
      return
    }

    this.onInputBlurChange.emit(inputValue)
  }

  cancel() {
    this.control.reset()
    this.onCancel.emit()
  }

  moveDate(increment: number) {
    let date = dayjs.utc(this.value, this.dateFormat).add(increment, 'day')
    if (
      date.format('YYYY-MM-DD') >= dayjs.utc(this.minimumDate).format('YYYY-MM-DD') &&
      date.format('YYYY-MM-DD') <= dayjs.utc(this.maximumDate).format('YYYY-MM-DD')
    ) {
      this.value = date.format(this.dateFormat)
      this.control.setValue(this.value)
      this.onInputChange.emit(date.toDate())
    }
  }

  protected readonly Validators = Validators
}
